import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { datasetsApi, projectsApi } from '../lib/api';
import { useOrg } from '../contexts/OrgContext';
import { exportToCsv, exportToGeoJson } from '../lib/utils';
import { useToast } from '../components/Toast';
import { exportToKML } from '../lib/kml';
import Layout from '../components/Layout';
import Breadcrumbs from '../components/Breadcrumbs';
import SortableTable from '../components/SortableTable';
import Pagination from '../components/Pagination';
import RenameField from '../components/RenameField';
import { Download, FileText, Map } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import { METHOD_COLORS } from '../lib/gis-types';

export const DATASETS_PAGE_SIZE = 100;

/** Query for GET /api/datasets: org/project scope, filters and the requested page. */
export function buildDatasetListParams({ orgId, projectId, filters, page, pageSize = DATASETS_PAGE_SIZE }) {
  return {
    organization_id: orgId || undefined,
    project_id: projectId || undefined,
    ...filters,
    page,
    page_size: pageSize,
  };
}

/** "Exported N datasets; M skipped (no location)" for the placed-point exports. */
export function exportSummary(label, { exported, skipped }) {
  if (!exported) return { type: 'warning', text: `No ${label} export: none of the ${skipped} datasets has a location (georeference a survey to place it).` };
  const text = `Exported ${exported} dataset${exported === 1 ? '' : 's'} to ${label}`;
  return skipped ? { type: 'warning', text: `${text}; ${skipped} skipped (no location)` } : { type: 'success', text };
}

function Datasets() {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { C } = useTheme();
  const { selectedOrgId, loading: orgLoading } = useOrg();
  const [datasets, setDatasets] = useState([]);
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState('');
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [renamingId, setRenamingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const pageSize = DATASETS_PAGE_SIZE;
  const [filters, setFilters] = useState({
    method_type: '',
    qc_status: '',
    search: ''
  });

  const loadProjects = useCallback(async () => {
    try {
      const projectsRes = await projectsApi.list(selectedOrgId);
      setProjects(projectsRes.data);
    } catch (error) {
      console.error('Failed to load projects:', error);
    }
  }, [selectedOrgId]);

  const loadDatasets = useCallback(async () => {
    if (!selectedOrgId) {
      setDatasets([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const params = buildDatasetListParams({ orgId: selectedOrgId, projectId: selectedProject, filters, page: currentPage, pageSize });
      const res = await datasetsApi.list(params);
      setDatasets(res.data.datasets);
      setTotal(res.data.total ?? res.data.datasets.length);
    } catch (error) {
      console.error('Failed to load datasets:', error);
    } finally {
      setLoading(false);
    }
  }, [selectedOrgId, selectedProject, filters, currentPage, pageSize]);

  // A new scope or filter starts again from the first page.
  useEffect(() => { setCurrentPage(1); }, [selectedOrgId, selectedProject, filters]);

  useEffect(() => {
    if (selectedOrgId) {
      loadProjects();
    } else {
      setProjects([]);
      setSelectedProject('');
    }
  }, [selectedOrgId, loadProjects]);

  useEffect(() => {
    if (!orgLoading) {
      loadDatasets();
    }
  }, [orgLoading, loadDatasets]);

  const getQCBadgeColor = (status) => {
    const colors = {
      passed: 'bg-green-100 text-green-800',
      pending: 'bg-yellow-100 text-yellow-800',
      failed: 'bg-red-100 text-red-800',
      processing: 'bg-blue-100 text-blue-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const tableColumns = [
    {
      key: 'name',
      label: 'Name',
      sortable: true,
      render: (value, row) => (row?.id && row.id === renamingId ? (
        <RenameField
          dataset={row}
          onCancel={() => setRenamingId(null)}
          onSaved={(name) => {
            setDatasets((list) => list.map((d) => (d.id === row.id ? { ...d, name } : d)));
            setRenamingId(null);
            showToast(`Renamed to ${name}`, 'success');
          }}
        />
      ) : <div className="text-sm font-medium">{value}</div>)
    },
    {
      key: 'method_type',
      label: 'Method',
      sortable: true,
      render: (value) => <div className="text-sm text-muted-foreground">{value}</div>
    },
    {
      key: 'qc_status',
      label: 'QC Status',
      sortable: true,
      render: (value, row) => (
        <span
          className={`px-2 py-1 text-xs font-semibold rounded-full ${getQCBadgeColor(value)}`}
          title={row?.status_reason || undefined}
          data-testid="qc-badge"
        >
          {value}
        </span>
      )
    },
    {
      key: 'created_at',
      label: 'Created',
      sortable: true,
      render: (value) => (
        <div className="text-sm text-muted-foreground">
          {new Date(value).toLocaleDateString()}
        </div>
      )
    },
    {
      key: 'id',
      label: 'Actions',
      sortable: false,
      render: (value, row) => (
        <div className="text-sm font-medium flex gap-3">
          <Link
            to={`/datasets/${value}`}
            className="text-secondary hover:underline"
            onClick={(e) => e.stopPropagation()}
            data-testid={`view-dataset-${value}`}
          >
            View
          </Link>
          <button
            type="button"
            className="text-secondary hover:underline"
            onClick={(e) => { e.stopPropagation(); setRenamingId(value); }}
            data-testid={`rename-${value}`}
          >
            Rename
          </button>
          <button
            type="button"
            className="hover:underline disabled:opacity-50"
            style={{ color: C.red }}
            disabled={deletingId === value}
            onClick={(e) => { e.stopPropagation(); handleDelete(row); }}
            data-testid={`delete-${value}`}
          >
            {deletingId === value ? 'Deleting…' : 'Delete'}
          </button>
        </div>
      )
    }
  ];

  const handleDelete = async (dataset) => {
    // Same confirmation as the dataset page: the file versions go with the dataset.
    if (!window.confirm(`Delete "${dataset.name}"? Its uploaded files are removed too. This cannot be undone.`)) return;
    setDeletingId(dataset.id);
    try {
      await datasetsApi.delete(dataset.id);
      setDatasets((list) => list.filter((d) => d.id !== dataset.id));
      setTotal((t) => Math.max(0, (t || 1) - 1));
      showToast(`Deleted ${dataset.name}`, 'success');
    } catch (err) {
      showToast(err?.response?.data?.detail || 'Could not delete the dataset', 'error');
    } finally {
      setDeletingId(null);
    }
  };

  const handleRowClick = (dataset) => {
    navigate(`/datasets/${dataset.id}`);
  };

  if (orgLoading || loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center py-12">
          <div className="text-lg">Loading...</div>
        </div>
      </Layout>
    );
  }

  if (!selectedOrgId) {
    return (
      <Layout>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Breadcrumbs />
          <div className="text-center py-12">
            <p className="text-muted-foreground mb-4">Select or create an organization to see datasets</p>
            <Link
              to="/"
              className="inline-block px-6 py-3 bg-primary text-primary-foreground rounded-md hover:opacity-90"
            >
              Go to Dashboard
            </Link>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Breadcrumbs />
        
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-6">
          <h1 className="text-3xl font-bold">Datasets</h1>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => exportToCsv(datasets, 'datasets_export.csv')}
              disabled={datasets.length === 0}
              className="px-4 py-2 border border-border rounded-md hover:bg-muted transition disabled:opacity-50 flex items-center gap-2"
              data-testid="export-csv-button"
              title="Export to CSV"
            >
              <FileText className="w-4 h-4" />
              CSV
            </button>
            <button
              onClick={() => { const r = exportSummary('GeoJSON', exportToGeoJson(datasets, 'datasets_export.geojson')); showToast(r.text, r.type); }}
              disabled={datasets.length === 0}
              className="px-4 py-2 border border-border rounded-md hover:bg-muted transition disabled:opacity-50 flex items-center gap-2"
              data-testid="export-geojson-button"
              title="Export to GeoJSON"
            >
              <Download className="w-4 h-4" />
              GeoJSON
            </button>
            <button
              onClick={() => { const r = exportSummary('KML', exportToKML(datasets, 'datasets_export.kml')); showToast(r.text, r.type); }}
              disabled={datasets.length === 0}
              className="px-4 py-2 border border-border rounded-md hover:bg-muted transition disabled:opacity-50 flex items-center gap-2"
              data-testid="export-kml-button"
              title="Export to KML"
            >
              <Map className="w-4 h-4" />
              KML
            </button>
            <Link
              to="/upload"
              className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:opacity-90"
              data-testid="upload-link"
            >
              Upload Data
            </Link>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-card border border-border rounded-lg p-4 mb-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Project</label>
              <select
                value={selectedProject}
                onChange={(e) => setSelectedProject(e.target.value)}
                className="w-full px-3 py-2 border border-input rounded-md text-sm"
                data-testid="project-filter"
              >
                <option value="">All Projects</option>
                {projects.map(project => (
                  <option key={project.id} value={project.id}>{project.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Method</label>
              <select
                value={filters.method_type}
                onChange={(e) => setFilters({...filters, method_type: e.target.value})}
                className="w-full px-3 py-2 border border-input rounded-md text-sm"
                data-testid="method-filter"
              >
                <option value="">All Methods</option>
                {Object.keys(METHOD_COLORS).map(method => (
                  <option key={method} value={method}>{method}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">QC Status</label>
              <select
                value={filters.qc_status}
                onChange={(e) => setFilters({...filters, qc_status: e.target.value})}
                className="w-full px-3 py-2 border border-input rounded-md text-sm"
                data-testid="qc-filter"
              >
                <option value="">All Status</option>
                <option value="passed">Passed</option>
                <option value="pending">Pending</option>
                <option value="processing">Processing</option>
                <option value="failed">Failed</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Search</label>
              <input
                type="text"
                value={filters.search}
                onChange={(e) => setFilters({...filters, search: e.target.value})}
                placeholder="Search datasets..."
                className="w-full px-3 py-2 border border-input rounded-md text-sm"
                data-testid="search-input"
              />
            </div>
          </div>
        </div>

        {/* Main Content */}
        {datasets.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-muted-foreground mb-4">No datasets found</p>
            <Link
              to="/upload"
              className="inline-block px-6 py-3 bg-primary text-primary-foreground rounded-md hover:opacity-90"
            >
              Upload Your First Dataset
            </Link>
          </div>
        ) : (
          <>
            <SortableTable
              data={datasets}
              columns={tableColumns}
              onRowClick={handleRowClick}
            />
            <Pagination
              currentPage={currentPage}
              totalPages={Math.ceil(total / pageSize)}
              totalItems={total}
              pageSize={pageSize}
              onPageChange={setCurrentPage}
            />
          </>
        )}
      </div>
    </Layout>
  );
}

export default Datasets;
